import { useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
  useLookups,
  createLookupValue,
  updateLookupValue,
  deleteLookupValue,
  reorderLookupValues,
  invalidateLookups,
} from "../../hooks/useLookups";
import { invalidateInitiatives } from "../../hooks/useInitiatives";
import { useCloseOnNavigate } from "../../hooks/useCloseOnNavigate";
import LookupCard from "./LookupCard";
import LookupFormDialog from "./LookupFormDialog";
import ConfirmDialog from "../ConfirmDialog";
import WarningPopup from "../WarningPopup";

const SECTIONS = [
  {
    label: "Horizons",
    fieldName: "Horizon",
    key: "horizons",
    bandColor: "error.main",
    hasColor: true,
  },
  {
    label: "Statuses",
    fieldName: "Status",
    key: "statuses",
    bandColor: "info.main",
    hasColor: true,
  },
  {
    label: "Priorities",
    fieldName: "Priority",
    key: "priorities",
    bandColor: "warning.main",
    hasColor: true,
  },
  {
    label: "Products",
    fieldName: "Product",
    key: "products",
    bandColor: "success.main",
    hasColor: true,
  },
  {
    label: "Impact Areas",
    fieldName: "Impact Area",
    key: "impactAreas",
    bandColor: "primary.main",
    hasColor: false,
  },
];

export default function ConfigureView({ can }) {
  const lookups = useLookups();
  const canCreate = can("lookup:create");
  const canEdit = can("lookup:edit");
  const canDelete = can("lookup:delete");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useCloseOnNavigate(() => {
    setDialogOpen(false);
    setConfirmOpen(false);
  });

  const styles = {
    root: { p: 3 },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
      gap: 2,
      maxWidth: 1200,
      mx: "auto",
    },
  };

  const handleAdd = (key) => {
    setActiveKey(key);
    setEditingItem(null);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleEdit = (key, item) => {
    setActiveKey(key);
    setEditingItem(item);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload) => {
    try {
      if (editingItem) {
        await updateLookupValue(activeKey, editingItem.id, payload);
        invalidateInitiatives();
      } else {
        await createLookupValue(activeKey, payload);
      }
      setDialogOpen(false);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    }
  };

  const handleDelete = (key, item) => {
    setPendingDelete({ key, item });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const { key, item } = pendingDelete;
    try {
      await deleteLookupValue(key, item.id);
    } catch (err) {
      if (err.status === 409) {
        setActionError(
          `"${item.name}" is currently in use and cannot be deleted.`,
        );
      } else if (err.status === 404) {
        setActionError(`"${item.name}" was already deleted.`);
      } else {
        setActionError(err.message || "Failed to delete. Please try again.");
      }
    }
  };

  const handleReorder = async (key, orderedIds) => {
    try {
      await reorderLookupValues(key, orderedIds);
    } catch (err) {
      setActionError(err.message || "Failed to reorder. Please try again.");
      invalidateLookups();
    }
  };

  const activeSection = SECTIONS.find((s) => s.key === activeKey);

  if (lookups.loading) {
    return (
      <Box sx={styles.root}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.grid}>
        {SECTIONS.map(({ label, key, bandColor }) => (
          <LookupCard
            key={key}
            label={label}
            items={lookups[key]}
            bandColor={bandColor}
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
            onAdd={() => handleAdd(key)}
            onEdit={(item) => handleEdit(key, item)}
            onDelete={(item) => handleDelete(key, item)}
            onReorder={(orderedIds) => handleReorder(key, orderedIds)}
          />
        ))}
      </Box>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete lookup value"
        message={
          pendingDelete
            ? `Are you sure you want to delete "${pendingDelete.item.name}"? This cannot be undone.`
            : ""
        }
      />
      <LookupFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        item={editingItem}
        sectionLabel={activeSection?.fieldName ?? ""}
        hasColor={activeSection?.hasColor ?? false}
        error={submitError}
      />
      <WarningPopup
        message={actionError}
        onClose={() => setActionError(null)}
      />
    </Box>
  );
}
